import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteCredential, getCredentials } from '../../../../api/endpoints/system';
import type { Credential } from '../../../../api/types/system';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { CredentialFormDialog } from '../CredentialFormDialog';

/** Hassas değeri liste için maskeler — asla tam gösterme, satırları ayırt edecek kadar
 *  (son 2 karakter). */
function maskSecret(value?: string | null): string {
  if (!value) return '—';
  return `••••••${value.slice(-2)}`;
}

export default function CredentialListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Credential | null>(null);
  const [search, setSearch] = useState('');

  const query = useQuery({ queryKey: ['system', 'credentials'], queryFn: () => getCredentials() });
  const rows: Credential[] = useMemo(() => query.data?.credentials ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.provider, row.providerName, row.providerType, row.serviceUsername, row.gsaId, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteCredential(id),
    invalidateKeys: [['system', 'credentials']],
    successMessage: 'Provider ayarı silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.provider ?? '';

  const columns: DataTableColumn<Credential>[] = [
    { id: 'provider', header: 'Sağlayıcı', cell: (r) => r.provider ?? r.providerName ?? '—' },
    { id: 'type', header: 'Tip', cell: (r) => r.providerType ?? '—' },
    { id: 'user', header: 'Servis Kullanıcı Adı', cell: (r) => r.serviceUsername ?? '—' },
    { id: 'pass', header: 'Servis Şifresi', cell: (r) => maskSecret(r.servicePassword) },
    { id: 'gsa', header: 'Firma ID', cell: (r) => r.gsaId ?? '—' },
    { id: 'status', header: 'Durum', cell: (r) => <StatusChip active={r.isActive} /> },
  ];

  return (
    <ListPageScaffold
      title="Provider Ayarları"
      createLabel="Yeni Provider Ayarı"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Sağlayıcı, tip, kullanıcı adı...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={
        rows.length === 0 ? 'Kayıtlı provider ayarı bulunamadı.' : 'Aramayla eşleşen provider ayarı bulunamadı.'
      }
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <CredentialFormDialog open={createOpen} credential={null} onClose={() => setCreateOpen(false)} />
      <CredentialFormDialog open={!!editing} credential={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Provider ayarını sil"
        description={`"${deletingName}" provider ayarını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
